import { useEffect, useRef } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { useGetInvoicePaymentInfoLazyQuery } from '../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/PublicBillingActionsRpc.hooks';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import Raven from 'raven-js';
import { Paths } from '../constants/Paths';
import { Metrics } from '../utils/Metrics';
import { useAppDispatch } from '../redux';
import { startInvoicePayment } from '../features/InvoicePayment/InvoicePaymentSlice';

// Needed to receive payment success message from Iframed checkout
function toIframeableUrl(url) {
  const parsed = new URL(url);

  // Already iframeable: checkout iframe URLs and dunning sessions
  // (auto-collect invoices resolve to a `/payments/update/` payment-method-update URL).
  if (parsed.pathname.includes('/iframe/') || parsed.pathname.includes('/payments/update/')) {
    return parsed.toString();
  }
  if (!parsed.pathname.includes('/purchase/')) {
    Raven.captureMessage(`PayInvoicePage: unexpected payment URL pathname: ${parsed.pathname}`);
  }
  parsed.pathname = parsed.pathname.replace('/purchase/', '/iframe/purchase/');
  return parsed.toString();
}
function buildPayInvoicePath(paymentUrl, navigationContext) {
  const formattedPaymentUrl = new URL(toIframeableUrl(paymentUrl));
  formattedPaymentUrl.searchParams.set('layout', 'billing-portal');
  const params = new URLSearchParams();
  params.set('url', formattedPaymentUrl.toString());
  if (navigationContext) {
    params.set('invoiceDetailsPath', navigationContext.invoiceDetailsPath);
    if (navigationContext.invoiceNumber) {
      params.set('invoiceNumber', navigationContext.invoiceNumber);
    }
  }
  return `${Paths.PAY_INVOICE}?${params.toString()}`;
}
export function usePayInvoice() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const pendingInvoiceIdRef = useRef(null);
  const navigationContextRef = useRef(null);
  const [fetchPaymentInfo, {
    data,
    loading,
    error
  }] = useGetInvoicePaymentInfoLazyQuery({
    fetchPolicy: 'no-cache'
  });
  useEffect(() => {
    const paymentUrl = data === null || data === void 0 ? void 0 : data.paymentUrl;
    const paidInvoiceId = pendingInvoiceIdRef.current;
    if (paymentUrl && paidInvoiceId !== null) {
      try {
        const target = buildPayInvoicePath(paymentUrl, navigationContextRef.current);
        dispatch(startInvoicePayment({
          paidInvoiceId
        }));
        history.push(target);
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    }
  }, [data, dispatch, history]);
  useEffect(() => {
    if (error) {
      Metrics.counter('pay-invoice-error').increment();
      FloatingAlertStore.addAlert({
        message: I18n.text('invoices.payNowError'),
        titleText: I18n.text('invoices.payNowErrorTitle'),
        type: 'danger'
      });
    }
  }, [error]);
  const payInvoice = (crmInvoiceId, navigationContext = null) => {
    pendingInvoiceIdRef.current = crmInvoiceId;
    navigationContextRef.current = navigationContext;
    Metrics.counter('pay-invoice-initiated').increment();
    fetchPaymentInfo({
      variables: {
        crmInvoiceId
      }
    });
  };
  return {
    payInvoice,
    loading
  };
}